import React, { useState, useEffect } from 'react';
import { 
  Plus, 
  Edit, 
  Trash2, 
  Save, 
  X, 
  FileText,
  Users
} from 'lucide-react';
import { Specialty, FormField } from '../types';
import { api } from '../services/api';
import toast from 'react-hot-toast';
import { motion, AnimatePresence } from 'framer-motion';

interface SpecialtiesManagerProps {
  onSpecialtyUpdate?: () => void;
}

const SpecialtiesManager: React.FC<SpecialtiesManagerProps> = ({ onSpecialtyUpdate }) => {
  const [specialties, setSpecialties] = useState<Specialty[]>([]);
  const [loading, setLoading] = useState(true);
  const [editingSpecialty, setEditingSpecialty] = useState<Specialty | null>(null);
  const [creatingSpecialty, setCreatingSpecialty] = useState(false);
  const [form, setForm] = useState({
    name: '',
    description: '',
    is_active: true,
    sort_order: 0,
    icon: 'stethoscope',
    form_config: [] as FormField[]
  });

  useEffect(() => {
    fetchSpecialties();
  }, []);

  const fetchSpecialties = async () => {
    try {
      const response = await api.get('/admin/specialties');
      setSpecialties(response.data);
    } catch (error) {
      console.error('Error fetching specialties:', error);
      toast.error('Error al cargar las especialidades');
    } finally {
      setLoading(false);
    }
  };

  const handleCreateSpecialty = () => {
    setForm({
      name: '',
      description: '',
      is_active: true,
      sort_order: specialties.length + 1,
      icon: 'stethoscope',
      form_config: []
    });
    setCreatingSpecialty(true);
    setEditingSpecialty(null);
  };

  const handleEditSpecialty = (specialty: Specialty) => {
    setForm({
      name: specialty.name,
      description: specialty.description || '',
      is_active: specialty.is_active,
      sort_order: specialty.sort_order,
      icon: specialty.icon || 'stethoscope',
      form_config: specialty.form_config
    });
    setEditingSpecialty(specialty);
    setCreatingSpecialty(false);
  };

  const handleSaveSpecialty = async () => {
    try {
      if (creatingSpecialty) {
        await api.post('/admin/specialties', form);
        toast.success('Especialidad creada exitosamente');
      } else if (editingSpecialty) {
        await api.put(`/admin/specialties/${editingSpecialty.id}`, form);
        toast.success('Especialidad actualizada exitosamente');
      }
      
      await fetchSpecialties();
      setCreatingSpecialty(false);
      setEditingSpecialty(null);
      onSpecialtyUpdate?.();
    } catch (error: any) {
      console.error('Error saving specialty:', error);
      toast.error(error.response?.data?.error || 'Error al guardar la especialidad');
    }
  };

  const handleDeleteSpecialty = async (specialty: Specialty) => {
    if (!window.confirm(`¿Estás seguro de que quieres eliminar la especialidad "${specialty.name}"?`)) {
      return;
    }

    try {
      await api.delete(`/admin/specialties/${specialty.id}`);
      toast.success('Especialidad eliminada exitosamente');
      await fetchSpecialties();
      onSpecialtyUpdate?.();
    } catch (error: any) {
      console.error('Error deleting specialty:', error);
      toast.error(error.response?.data?.error || 'Error al eliminar la especialidad');
    }
  };

  const addFormField = () => {
    const newField: FormField = {
      id: `field_${Date.now()}`,
      type: 'text',
      label: '',
      required: false,
      placeholder: ''
    };
    setForm(prev => ({
      ...prev,
      form_config: [...prev.form_config, newField]
    }));
  };

  const updateFormField = (index: number, field: Partial<FormField>) => {
    setForm(prev => ({
      ...prev,
      form_config: prev.form_config.map((f, i) => 
        i === index ? { ...f, ...field } : f
      )
    }));
  };

  const removeFormField = (index: number) => {
    setForm(prev => ({
      ...prev,
      form_config: prev.form_config.filter((_, i) => i !== index)
    }));
  };

  const fieldTypes = [
    { value: 'text', label: 'Texto' },
    { value: 'textarea', label: 'Área de texto' },
    { value: 'email', label: 'Email' },
    { value: 'tel', label: 'Teléfono' },
    { value: 'number', label: 'Número' },
    { value: 'date', label: 'Fecha' },
    { value: 'select', label: 'Selección' },
    { value: 'radio', label: 'Botones de radio' },
    { value: 'checkbox', label: 'Casillas de verificación' }
  ];

  const availableIcons = [
    { value: 'stethoscope', icon: '🩺' },
    { value: 'heart', icon: '❤️' },
    { value: 'brain', icon: '🧠' },
    { value: 'eye', icon: '👁️' },
    { value: 'tooth', icon: '🦷' },
    { value: 'baby', icon: '👶' },
    { value: 'woman', icon: '👩' },
    { value: 'bone', icon: '🦴' },
    { value: 'lungs', icon: '🫁' },
    { value: 'pill', icon: '💊' },
    { value: 'syringe', icon: '💉' },
    { value: 'microscope', icon: '🔬' },
    { value: 'x-ray', icon: '📷' },
    { value: 'scissors', icon: '✂️' },
    { value: 'bandage', icon: '🩹' },
    { value: 'ear', icon: '👂' },
    { value: 'nose', icon: '👃' },
    { value: 'mouth', icon: '👄' },
    { value: 'hand', icon: '✋' },
    { value: 'foot', icon: '🦶' },
    { value: 'spine', icon: '🦴' },
    { value: 'kidney', icon: '🫘' },
    { value: 'liver', icon: '🫀' },
    { value: 'stomach', icon: '🫃' },
    { value: 'dna', icon: '🧬' },
    { value: 'virus', icon: '🦠' },
    { value: 'bacteria', icon: '🦠' },
    { value: 'thermometer', icon: '🌡️' },
    { value: 'crutch', icon: '🩼' },
    { value: 'wheelchair', icon: '♿' },
    { value: 'ambulance', icon: '🚑' },
    { value: 'hospital', icon: '🏥' },
    { value: 'cross', icon: '➕' },
    { value: 'shield', icon: '🛡️' },
    { value: 'star', icon: '⭐' },
    { value: 'sun', icon: '☀️' },
    { value: 'moon', icon: '🌙' },
    { value: 'flower', icon: '🌸' },
    { value: 'leaf', icon: '🍃' },
    { value: 'water', icon: '💧' },
    { value: 'fire', icon: '🔥' },
    { value: 'lightning', icon: '⚡' },
    { value: 'snowflake', icon: '❄️' },
    { value: 'rainbow', icon: '🌈' },
    { value: 'butterfly', icon: '🦋' },
    { value: 'bird', icon: '🐦' },
    { value: 'fish', icon: '🐟' },
    { value: 'cat', icon: '🐱' },
    { value: 'dog', icon: '🐶' },
    { value: 'rabbit', icon: '🐰' },
    { value: 'bear', icon: '🐻' },
    { value: 'panda', icon: '🐼' },
    { value: 'koala', icon: '🐨' },
    { value: 'monkey', icon: '🐵' },
    { value: 'lion', icon: '🦁' },
    { value: 'tiger', icon: '🐅' },
    { value: 'elephant', icon: '🐘' },
    { value: 'giraffe', icon: '🦒' },
    { value: 'zebra', icon: '🦓' },
    { value: 'horse', icon: '🐴' },
    { value: 'cow', icon: '🐄' },
    { value: 'pig', icon: '🐷' },
    { value: 'sheep', icon: '🐑' },
    { value: 'goat', icon: '🐐' },
    { value: 'chicken', icon: '🐔' },
    { value: 'duck', icon: '🦆' },
    { value: 'owl', icon: '🦉' },
    { value: 'eagle', icon: '🦅' },
    { value: 'penguin', icon: '🐧' },
    { value: 'whale', icon: '🐋' },
    { value: 'dolphin', icon: '🐬' },
    { value: 'shark', icon: '🦈' },
    { value: 'octopus', icon: '🐙' },
    { value: 'crab', icon: '🦀' },
    { value: 'lobster', icon: '🦞' },
    { value: 'shrimp', icon: '🦐' },
    { value: 'snail', icon: '🐌' },
    { value: 'spider', icon: '🕷️' },
    { value: 'ant', icon: '🐜' },
    { value: 'bee', icon: '🐝' },
    { value: 'ladybug', icon: '🐞' },
    { value: 'cricket', icon: '🦗' },
    { value: 'dragonfly', icon: '🦟' },
    { value: 'mosquito', icon: '🦟' },
    { value: 'fly', icon: '🪰' },
    { value: 'worm', icon: '🪱' },
    { value: 'microbe', icon: '🦠' }
  ];

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex justify-between items-center">
        <div>
          <h2 className="text-2xl font-bold text-gray-900">Gestión de Especialidades</h2>
          <p className="text-gray-600">Administra las especialidades médicas y sus formularios personalizados</p>
        </div>
        <button
          onClick={handleCreateSpecialty}
          className="inline-flex items-center px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 transition-colors"
        >
          <Plus className="h-4 w-4 mr-2" />
          Nueva Especialidad
        </button>
      </div>

      {/* Specialties List */}
      <div className="grid gap-4">
        {specialties.map((specialty) => (
          <motion.div
            key={specialty.id}
            className="bg-white rounded-lg border border-gray-200 p-6"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.3 }}
          >
            <div className="flex justify-between items-start">
              <div className="flex-1">
                <div className="flex items-center space-x-3 mb-2">
                  <h3 className="text-lg font-semibold text-gray-900">{specialty.name}</h3>
                  <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${
                    specialty.is_active 
                      ? 'bg-green-100 text-green-800' 
                      : 'bg-red-100 text-red-800'
                  }`}>
                    {specialty.is_active ? 'Activa' : 'Inactiva'}
                  </span>
                  <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                    <Users className="h-3 w-3 mr-1" />
                    {specialty.professional_count} profesional{specialty.professional_count !== 1 ? 'es' : ''}
                  </span>
                </div>
                {specialty.description && (
                  <p className="text-gray-600 mb-3">{specialty.description}</p>
                )}
                <div className="flex items-center space-x-4 text-sm text-gray-500">
                  <span className="flex items-center">
                    <FileText className="h-4 w-4 mr-1" />
                    {specialty.form_config.length} campo{specialty.form_config.length !== 1 ? 's' : ''} de formulario
                  </span>
                  <span>Orden: {specialty.sort_order}</span>
                </div>
              </div>
              <div className="flex space-x-2">
                <button
                  onClick={() => handleEditSpecialty(specialty)}
                  className="p-2 text-gray-400 hover:text-blue-600 transition-colors"
                  title="Editar especialidad"
                >
                  <Edit className="h-4 w-4" />
                </button>
                <button
                  onClick={() => handleDeleteSpecialty(specialty)}
                  className="p-2 text-gray-400 hover:text-red-600 transition-colors"
                  title="Eliminar especialidad"
                >
                  <Trash2 className="h-4 w-4" />
                </button>
              </div>
            </div>
          </motion.div>
        ))}
      </div>

      {/* Create/Edit Modal */}
      <AnimatePresence>
        {(creatingSpecialty || editingSpecialty) && (
          <motion.div
            className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
          >
            <motion.div
              className="bg-white rounded-lg max-w-4xl w-full max-h-[90vh] overflow-y-auto"
              initial={{ scale: 0.95, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.95, opacity: 0 }}
            >
              <div className="p-6">
                <div className="flex justify-between items-center mb-6">
                  <h3 className="text-xl font-bold text-gray-900">
                    {creatingSpecialty ? 'Nueva Especialidad' : 'Editar Especialidad'}
                  </h3>
                  <button
                    onClick={() => {
                      setCreatingSpecialty(false);
                      setEditingSpecialty(null);
                    }}
                    className="p-2 text-gray-400 hover:text-gray-600 transition-colors"
                  >
                    <X className="h-5 w-5" />
                  </button>
                </div>

                <div className="space-y-6">
                  {/* Basic Information */}
                  <div className="grid md:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Nombre *
                      </label>
                      <input
                        type="text"
                        value={form.name}
                        onChange={(e) => setForm(prev => ({ ...prev, name: e.target.value }))}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                        placeholder="Ej: Psicología"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Orden de visualización
                      </label>
                      <input
                        type="number"
                        value={form.sort_order}
                        onChange={(e) => setForm(prev => ({ ...prev, sort_order: parseInt(e.target.value) || 0 }))}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                      />
                    </div>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Descripción
                    </label>
                    <textarea
                      value={form.description}
                      onChange={(e) => setForm(prev => ({ ...prev, description: e.target.value }))}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                      rows={3}
                      placeholder="Descripción de la especialidad..."
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Icono
                    </label>
                    <div className="grid grid-cols-8 gap-2 max-h-60 overflow-y-auto border border-gray-300 rounded-lg p-3">
                      {availableIcons.map((iconOption) => (
                        <button
                          key={iconOption.value}
                          type="button"
                          onClick={() => setForm(prev => ({ ...prev, icon: iconOption.value }))}
                          className={`flex items-center justify-center p-3 rounded-lg border-2 transition-colors ${
                            form.icon === iconOption.value
                              ? 'border-primary-500 bg-primary-50'
                              : 'border-gray-200 hover:border-gray-300'
                          }`}
                          title={iconOption.value}
                        >
                          <span className="text-2xl">{iconOption.icon}</span>
                        </button>
                      ))}
                    </div>
                  </div>

                  <div className="flex items-center">
                    <input
                      type="checkbox"
                      id="is_active"
                      checked={form.is_active}
                      onChange={(e) => setForm(prev => ({ ...prev, is_active: e.target.checked }))}
                      className="h-4 w-4 text-primary-600 focus:ring-primary-500 border-gray-300 rounded"
                    />
                    <label htmlFor="is_active" className="ml-2 text-sm text-gray-700">
                      Especialidad activa
                    </label>
                  </div>

                  {/* Form Configuration */}
                  <div>
                    <div className="flex justify-between items-center mb-4">
                      <h4 className="text-lg font-semibold text-gray-900">Configuración del Formulario</h4>
                      <button
                        onClick={addFormField}
                        className="inline-flex items-center px-3 py-1 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 transition-colors text-sm"
                      >
                        <Plus className="h-4 w-4 mr-1" />
                        Agregar Campo
                      </button>
                    </div>

                    <div className="space-y-4">
                      {form.form_config.map((field, index) => (
                        <div key={field.id} className="border border-gray-200 rounded-lg p-4">
                          <div className="flex justify-between items-start mb-3">
                            <h5 className="font-medium text-gray-900">Campo {index + 1}</h5>
                            <button
                              onClick={() => removeFormField(index)}
                              className="p-1 text-gray-400 hover:text-red-600 transition-colors"
                            >
                              <X className="h-4 w-4" />
                            </button>
                          </div>

                          <div className="grid md:grid-cols-2 gap-4">
                            <div>
                              <label className="block text-sm font-medium text-gray-700 mb-1">
                                Tipo de campo *
                              </label>
                              <select
                                value={field.type}
                                onChange={(e) => updateFormField(index, { type: e.target.value as any })}
                                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                              >
                                {fieldTypes.map(type => (
                                  <option key={type.value} value={type.value}>{type.label}</option>
                                ))}
                              </select>
                            </div>
                            <div>
                              <label className="block text-sm font-medium text-gray-700 mb-1">
                                Etiqueta *
                              </label>
                              <input
                                type="text"
                                value={field.label}
                                onChange={(e) => updateFormField(index, { label: e.target.value })}
                                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                                placeholder="Ej: Edad del paciente"
                              />
                            </div>
                          </div>

                          <div className="grid md:grid-cols-2 gap-4 mt-3">
                            <div>
                              <label className="block text-sm font-medium text-gray-700 mb-1">
                                Placeholder
                              </label>
                              <input
                                type="text"
                                value={field.placeholder || ''}
                                onChange={(e) => updateFormField(index, { placeholder: e.target.value })}
                                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                                placeholder="Texto de ayuda..."
                              />
                            </div>
                            <div className="flex items-center">
                              <input
                                type="checkbox"
                                id={`required_${index}`}
                                checked={field.required}
                                onChange={(e) => updateFormField(index, { required: e.target.checked })}
                                className="h-4 w-4 text-primary-600 focus:ring-primary-500 border-gray-300 rounded"
                              />
                              <label htmlFor={`required_${index}`} className="ml-2 text-sm text-gray-700">
                                Campo requerido
                              </label>
                            </div>
                          </div>

                          {/* Options for select, radio, checkbox */}
                          {(field.type === 'select' || field.type === 'radio' || field.type === 'checkbox') && (
                            <div className="mt-3">
                              <label className="block text-sm font-medium text-gray-700 mb-1">
                                Opciones (una por línea)
                              </label>
                              <textarea
                                value={field.options?.join('\n') || ''}
                                onChange={(e) => {
                                  // Don't filter out empty lines while typing - let user create new lines
                                  const lines = e.target.value.split('\n');
                                  updateFormField(index, { 
                                    options: lines
                                  });
                                }}
                                onBlur={(e) => {
                                  // Filter out empty lines only when user finishes editing
                                  const lines = e.target.value.split('\n').filter(opt => opt.trim());
                                  updateFormField(index, { 
                                    options: lines
                                  });
                                }}
                                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                                rows={3}
                                placeholder="Opción 1&#10;Opción 2&#10;Opción 3"
                              />
                            </div>
                          )}

                          {/* Validation for number fields */}
                          {field.type === 'number' && (
                            <div className="grid md:grid-cols-2 gap-4 mt-3">
                              <div>
                                <label className="block text-sm font-medium text-gray-700 mb-1">
                                  Valor mínimo
                                </label>
                                <input
                                  type="number"
                                  value={field.validation?.min || ''}
                                  onChange={(e) => updateFormField(index, { 
                                    validation: { ...field.validation, min: parseInt(e.target.value) || undefined }
                                  })}
                                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                                />
                              </div>
                              <div>
                                <label className="block text-sm font-medium text-gray-700 mb-1">
                                  Valor máximo
                                </label>
                                <input
                                  type="number"
                                  value={field.validation?.max || ''}
                                  onChange={(e) => updateFormField(index, { 
                                    validation: { ...field.validation, max: parseInt(e.target.value) || undefined }
                                  })}
                                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                                />
                              </div>
                            </div>
                          )}
                        </div>
                      ))}
                    </div>
                  </div>

                  {/* Actions */}
                  <div className="flex justify-end space-x-3 pt-6 border-t">
                    <button
                      onClick={() => {
                        setCreatingSpecialty(false);
                        setEditingSpecialty(null);
                      }}
                      className="px-4 py-2 text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200 transition-colors"
                    >
                      Cancelar
                    </button>
                    <button
                      onClick={handleSaveSpecialty}
                      disabled={!form.name.trim()}
                      className="inline-flex items-center px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      <Save className="h-4 w-4 mr-2" />
                      {creatingSpecialty ? 'Crear' : 'Guardar'}
                    </button>
                  </div>
                </div>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default SpecialtiesManager;
